import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { Chart, ChartOptions, Plugin, TooltipItem } from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

ChartJS.defaults.font.family =
  'Inter, apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

export const CHART_COLORS = {
  defaultDark: '#505fee',
  compareLight: '#99e7fc',
  tertiary: '#D1308B',
  quaternary: '#8746ff',
  quinary: '#ffc400',
  gridLine: '#f8f9fa',
  axisLabel: '#7a869a',
  mapBase: '#e9edfd',
  mapHoverHighlight: '#647ef2',
  mapHoverBase: '#d3dafb',
  labelDark: '#181b1f',
  tooltipSurface: '#181b1f',
} as const;

export const CHART_HEIGHT_PX = 168;

export const CHART_PLACEHOLDER_CLASS_NAME = 'tw-h-[168px] tw-w-full';

export const MAP_CHART_HEIGHT_PX = 320;

export const MAP_PLACEHOLDER_CLASS_NAME = 'tw-h-[320px] tw-w-full';

export const BEST_TIME_TO_POST_PLACEHOLDER_CLASS_NAME =
  'tw-min-h-[224px] tw-w-full tw-flex-1';

export const DEMOGRAPHICS_PLACEHOLDER_CLASS_NAME =
  'tw-min-h-[200px] tw-w-full tw-flex-1';

const CROSSHAIR_DASH_PATTERN: [number, number] = [4, 4];

function resolveCrosshairXPixels(chart: Chart): number | undefined {
  const active = chart.getActiveElements();
  if (active.length === 0) {
    return undefined;
  }
  const xPixels: number[] = [];
  for (const item of active) {
    const { element } = item;
    if (
      typeof element === 'object' &&
      element !== null &&
      'getCenterPoint' in element &&
      typeof (
        element as { getCenterPoint?: (final?: boolean) => { x?: unknown } }
      ).getCenterPoint === 'function'
    ) {
      const { x } = (
        element as {
          getCenterPoint: (final?: boolean) => { x?: unknown };
        }
      ).getCenterPoint();
      if (typeof x === 'number') {
        xPixels.push(x);
      }
      continue;
    }
    if (typeof element === 'object' && element !== null && 'x' in element) {
      const xVal = (element as { x: unknown }).x;
      if (typeof xVal === 'number') {
        xPixels.push(xVal);
      }
    }
  }

  const xPixel =
    xPixels.length > 1
      ? xPixels.reduce((a, b) => a + b, 0) / xPixels.length
      : xPixels[0];
  return typeof xPixel === 'number' ? xPixel : undefined;
}

const cartesianVerticalCrosshairPluginImplementation: Plugin = {
  id: 'cartesianVerticalCrosshair',
  beforeDatasetsDraw(chart) {
    const xPixel = resolveCrosshairXPixels(chart);
    if (xPixel === undefined) {
      return;
    }

    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash(CROSSHAIR_DASH_PATTERN);
    ctx.lineWidth = 1;
    ctx.strokeStyle = `${CHART_COLORS.axisLabel}59`;
    ctx.moveTo(xPixel, chartArea.top);
    ctx.lineTo(xPixel, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

export const lineChartVerticalCrosshairPlugin =
  cartesianVerticalCrosshairPluginImplementation as Plugin<'line'>;

export const barChartVerticalCrosshairPlugin =
  cartesianVerticalCrosshairPluginImplementation as Plugin<'bar'>;

export const DEFAULT_PALETTE = [
  CHART_COLORS.defaultDark,
  CHART_COLORS.compareLight,
  CHART_COLORS.tertiary,
  CHART_COLORS.quaternary,
  CHART_COLORS.quinary,
];

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      titleFont: { size: 12 },
      bodyFont: { size: 12 },
    },
  },
};

export const getCartesianChartOptions = (options?: {
  showGridX?: boolean;
  showGridY?: boolean;
}) => {
  const { showGridX = false, showGridY = true } = options ?? {};
  return {
    ...baseOptions,
    scales: {
      x: {
        grid: { display: showGridX, color: CHART_COLORS.gridLine },
        ticks: {
          font: { size: 10 },
          color: CHART_COLORS.axisLabel,
          autoSkip: false,
          maxRotation: 0,
        },
      },
      y: {
        min: 0,
        grid: { display: showGridY, color: CHART_COLORS.gridLine },
        ticks: {
          font: { size: 10 },
          color: CHART_COLORS.axisLabel,
        },
      },
    },
  } as ChartOptions<'line'>;
};

export const getDoughnutChartOptions = (): ChartOptions<'doughnut'> => ({
  ...baseOptions,
  cutout: '80%',
  plugins: {
    ...baseOptions.plugins,
    tooltip: {
      ...baseOptions.plugins.tooltip,
      position: 'nearest' as const,
      backgroundColor: CHART_COLORS.tooltipSurface,
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderWidth: 0,
      padding: 8,
      cornerRadius: 8,
      caretPadding: 0,
      displayColors: false,
      titleMarginBottom: 0,
      callbacks: {
        title: () => '',
        label: (ctx: TooltipItem<'doughnut'>) => {
          const unit = (ctx.dataset as { unit?: string }).unit ?? '';
          return `${ctx.label} ${ctx.parsed} ${unit}`;
        },
      },
    },
  },
});

export function resolveDatasetColor(
  datasetIndex: number,
  datasetColor?: string,
  colorsProp?: string[],
): string {
  if (datasetColor) return datasetColor;
  if (colorsProp?.[datasetIndex]) return colorsProp[datasetIndex];
  return DEFAULT_PALETTE[datasetIndex % DEFAULT_PALETTE.length];
}
